import { CustomAppEvents } from 'core/common/entities';
import { checkIsClientSide } from 'core/common/utils/checkIsClientSide';
import { CookieConsentService } from 'core/cookie-consent/interfaces';
import { AnalyticsPixelId, AxonEventNames, AxonMethods } from '../../entities';
import { AxonService } from '../../interfaces';

const noop: AxonMethods & { isAvailable: false } = {
  isAvailable: false,
  init: () => {},
  track: () => {},
};

export class HttpAxonService implements AxonService {
  private readonly passedPixelIds: Array<AnalyticsPixelId> = [];
  private initializedPixelIds: Array<AnalyticsPixelId> = [];

  constructor(
    private readonly cookieConsentService: CookieConsentService,
    pixelIds?: Array<AnalyticsPixelId>,
  ) {
    this.passedPixelIds = !!pixelIds?.length ? pixelIds : [];
    this.cookieConsentService.addConsentLoadListener(this.onConsentLoad(this.passedPixelIds));
    this.cookieConsentService.addConsentChangeListener(this.onConsentChange(this.passedPixelIds));
  }

  isAxonRequired(): boolean {
    return !!this.passedPixelIds.length;
  }

  isAxonAvailable(): boolean {
    return this.axon.isAvailable;
  }

  track(eventName: AxonEventNames, data?: Record<string, unknown>): void {
    this.axon.track(eventName, data);
  }

  private get axon(): AxonMethods & { isAvailable: boolean } {
    if (!checkIsClientSide() || !window.axon) {
      return noop;
    }

    const axon = window.axon;

    return {
      isAvailable: true,
      init: () => axon('init'),
      track: (eventName: AxonEventNames, data?: Record<string, unknown>) =>
        axon('track', eventName, data),
    };
  }

  private onConsentLoad =
    (pixels: Array<AnalyticsPixelId>) => (consentState: { TARGETING: boolean }) => {
      if (consentState.TARGETING) {
        this.initializePixels(pixels);
      }
    };

  private onConsentChange =
    (pixels: Array<AnalyticsPixelId>) => (consentState: { TARGETING: boolean }) => {
      if (consentState.TARGETING) {
        this.initializePixels(pixels);
      }
    };

  private initializePixels(pixels: Array<AnalyticsPixelId>) {
    if (this.axon.isAvailable) {
      this.doInitializePixels(pixels);
      return;
    }

    document.addEventListener(
      CustomAppEvents.AXON_LOADED,
      () => {
        this.doInitializePixels(pixels);
      },
      { once: true },
    );
  }

  private doInitializePixels(pixelsToInitialize: Array<AnalyticsPixelId>) {
    pixelsToInitialize.forEach((pixel) => {
      if (this.initializedPixelIds.includes(pixel)) {
        return;
      }

      this.initializedPixelIds.push(pixel);
    });
    this.axon.init();
  }
}
